import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Badge,
  Spinner
} from 'react-bootstrap';
import {
  FaBell,
  FaCalendarAlt,
  FaChartLine,
  FaClock,
  FaArrowUp,
  FaCheckCircle,
  FaExclamationCircle,
  FaUsers,
  FaDollarSign,
  FaChartBar,
  FaWallet,
  FaBuilding,
  FaExclamationTriangle,
  FaClipboardList,
  FaChartPie,
  FaTimes,
  FaUserAlt
} from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, RadialBarChart, RadialBar } from 'recharts';
import { API_URL, API_ENDPOINTS } from './config/api';
import './inicio.css';

// Componente DonutChart reutilizable
const DonutChart = ({ data, centerValue, centerLabel, colors, height = 180 }) => (
  <div className="cohesity-donut-wrapper">
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={55}
          outerRadius={75}
          paddingAngle={3}
          dataKey="value"
          strokeWidth={0}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={colors[index % colors.length]}
              style={{ filter: 'drop-shadow(0 0 8px rgba(0, 212, 170, 0.3))' }}
            />
          ))}
        </Pie>
        <Tooltip
          formatter={(value, name) => {
            if (typeof value === 'number') {
              return [value, name];
            }
            return [value, name];
          }}
          contentStyle={{
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '8px',
            color: '#e2e8f0'
          }}
        />
      </PieChart>
    </ResponsiveContainer>
    <div className="cohesity-donut-center">
      <span className="cohesity-center-value">{centerValue}</span>
      <span className="cohesity-center-label">{centerLabel}</span>
    </div>
  </div>
);

// Componente MiniGauge con contraste visible (completado vs faltante)
const MiniGauge = ({ value, maxValue = 100, color, label }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);

  // Data con el valor completado y el restante
  const data = [
    { name: 'completed', value: percentage },
    { name: 'remaining', value: 100 - percentage }
  ];

  return (
    <div className="mini-gauge-container">
      <ResponsiveContainer width={70} height={70}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={22}
            outerRadius={32}
            startAngle={90}
            endAngle={-270}
            paddingAngle={0}
            dataKey="value"
            strokeWidth={0}
          >
            <Cell fill={color} style={{ filter: `drop-shadow(0 0 6px ${color}50)` }} />
            <Cell fill="rgba(255,255,255,0.15)" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="mini-gauge-label">
        <span style={{ color: color }}>{label}</span>
      </div>
    </div>
  );
};

// Componente Modal de Aviso (Custom - sin Bootstrap Modal)
const NoticeModal = ({ show, onClose, notice }) => {
  if (!show || !notice) return null;

  return (
    <div className="urbanq-modal-overlay" onClick={onClose}>
      <div className="urbanq-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="urbanq-modal-header">
          <h2 className="urbanq-modal-title">
            <FaBell className="urbanq-modal-icon" />
            {notice.titulo || notice.title}
          </h2>
          <button className="urbanq-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="urbanq-modal-body">
          <div className="urbanq-modal-date">
            <FaCalendarAlt className="me-2" />
            {new Date(notice.fecha || notice.created_at).toLocaleDateString('es-ES', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
          <div className="urbanq-modal-text">
            {notice.descripcion || notice.description || 'Sin descripción disponible'}
          </div>
        </div>
        <div className="urbanq-modal-footer">
          <button className="urbanq-modal-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const Inicio = ({ user, notificaciones, pagos, alicuotas, stats: propStats, setActiveSection }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pagosRealizados: 0,
    pagosPendientes: 0,
    pendientesAprobacion: 0,
    total: 0,
    progreso: 0
  });
  const [userInfo, setUserInfo] = useState({
    nombre: user?.name || 'Administrador',
    apartamento: user?.apartment || 'Admin',
    ultimoPago: '-',
    saldoPendiente: '$0.00'
  });

  // Estados para datos reales del sistema
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalApartments, setTotalApartments] = useState(0);
  const [totalPendingAmount, setTotalPendingAmount] = useState(0);
  const [totalCollected, setTotalCollected] = useState(0);
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [collectionRate, setCollectionRate] = useState(0);
  const [allPayments, setAllPayments] = useState([]); // Todos los pagos para la tabla
  const [activeUsers, setActiveUsers] = useState(0);
  const [inactiveUsers, setInactiveUsers] = useState(0);

  // Estado para modal de avisos
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);

  // Colores estilo Cohesity
  const COLORS = {
    primary: '#00d4aa',
    secondary: '#6366f1',
    success: '#22c55e',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    pending: '#f97316',
    purple: '#a855f7',
    pink: '#ec4899'
  };

  // Función para obtener el token
  const getToken = () => {
    return localStorage.getItem('token') || '';
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Función para obtener fecha actual formateada
  const getCurrentDate = () => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date().toLocaleDateString('es-ES', options);
  };

  // Abrir modal de aviso
  const handleNoticeClick = (notice) => {
    setSelectedNotice(notice);
    setShowNoticeModal(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = getToken();
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };

        // 1. Obtener usuarios reales
        try {
          const usersResponse = await fetch(API_ENDPOINTS.users, { headers });
          if (usersResponse.ok) {
            const usersData = await usersResponse.json();
            const usersArray = Array.isArray(usersData.users) ? usersData.users : Array.isArray(usersData) ? usersData : [];

            const residents = usersArray.filter(u => u.role === 'User');
            setTotalUsers(residents.length);

            const active = residents.filter(u => u.account_status !== 'suspended' && u.account_status !== 'inactive');
            setActiveUsers(active.length);
            setInactiveUsers(residents.length - active.length);

            const uniqueApartments = new Set(
              residents.map(u => u.apartment).filter(apt => apt && apt !== '-')
            );
            setTotalApartments(uniqueApartments.size || residents.length);
          }
        } catch (err) {
          console.error('Error al cargar usuarios:', err);
          setTotalUsers(0);
          setTotalApartments(0);
        }

        // 2. Procesar datos de alícuotas/pagos
        if (alicuotas && alicuotas.length > 0) {
          const paymentsData = alicuotas.map(a => ({
            id: a.id,
            created_at: a.fechaOriginal || a.fechaVencimiento || a.created_at,
            payment_date: a.fechaPago || a.fechaOriginal || a.created_at,
            amount: parseFloat(a.monto) || 0,
            status: a.estado === 'Pagado' ? 'Paid' :
                    a.estado === 'Pendiente de validación' ? 'Validation' : 'Pending',
            concept: a.concepto,
            user_name: a.usuario,
            apartment: a.apartamento,
            due_date: a.fechaVencimiento
          }));

          const pagados = paymentsData.filter(p => p.status === 'Paid');
          const pendientes = paymentsData.filter(p => p.status === 'Pending');
          const pendientesValidacion = paymentsData.filter(p => p.status === 'Validation');
          const total = paymentsData.length;

          const montoRecaudado = pagados.reduce((sum, p) => sum + p.amount, 0);
          const montoPendiente = pendientes.reduce((sum, p) => sum + p.amount, 0);
          const montoTotal = paymentsData.reduce((sum, p) => sum + p.amount, 0);

          const tasaCobro = montoTotal > 0 ? (montoRecaudado / montoTotal) * 100 : 0;

          const currentMonth = new Date().getMonth();
          const currentYear = new Date().getFullYear();
          const pagosMesActual = pagados.filter(p => {
            const paymentDate = new Date(p.payment_date || p.created_at);
            return paymentDate.getMonth() === currentMonth &&
                   paymentDate.getFullYear() === currentYear;
          });
          const ingresoMensual = pagosMesActual.reduce((sum, p) => sum + p.amount, 0);

          setStats({
            pagosRealizados: pagados.length,
            pagosPendientes: pendientes.length,
            pendientesAprobacion: pendientesValidacion.length,
            total: total,
            progreso: total > 0 ? Math.round(((pagados.length + pendientesValidacion.length) / total) * 100) : 0
          });

          setTotalPendingAmount(montoPendiente);
          setTotalCollected(montoRecaudado);
          setMonthlyIncome(ingresoMensual);
          setCollectionRate(tasaCobro);
          setAllPayments(paymentsData);

          // Obtener último pago
          if (pagados.length > 0) {
            const ultimoPago = pagados.sort((a, b) =>
              new Date(b.payment_date || b.created_at) - new Date(a.payment_date || a.created_at)
            )[0];
            setUserInfo(prev => ({
              ...prev,
              ultimoPago: formatDate(ultimoPago.payment_date || ultimoPago.created_at)
            }));
          }
        }
      } catch (err) {
        console.error('Error al cargar datos:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [alicuotas, user]);

  // Datos para gráfico de estado de pagos (donut compacto)
  const paymentStatusData = [
    { name: 'Pagados', value: stats.pagosRealizados, amount: totalCollected },
    { name: 'Validación', value: stats.pendientesAprobacion, amount: 0 },
    { name: 'Pendientes', value: stats.pagosPendientes, amount: totalPendingAmount }
  ].filter(d => d.value > 0);

  // Datos para gráfico de barras mensual - CORREGIDO
  const generateMonthlyData = () => {
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    let monthlyData = [];
    for (let i = 4; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      const year = currentMonth - i < 0 ? currentYear - 1 : currentYear;
      monthlyData.push({
        name: monthNames[monthIndex],
        monto: 0,
        month: monthIndex,
        year: year
      });
    }

    if (alicuotas && alicuotas.length > 0) {
      alicuotas.forEach(a => {
        if (a.estado === 'Pagado') {
          // Usar múltiples campos de fecha como fallback
          const dateStr = a.fechaPago || a.fechaOriginal || a.fechaVencimiento || a.created_at;
          if (dateStr) {
            const paymentDate = new Date(dateStr);
            const paymentMonth = paymentDate.getMonth();
            const paymentYear = paymentDate.getFullYear();

            // Buscar el índice correcto en monthlyData
            const dataIndex = monthlyData.findIndex(
              d => d.month === paymentMonth && d.year === paymentYear
            );

            if (dataIndex !== -1) {
              monthlyData[dataIndex].monto += parseFloat(a.monto) || 0;
            }
          }
        }
      });
    }

    return monthlyData;
  };

  const monthlyChartData = generateMonthlyData();
  const totalMonthlyRevenue = monthlyChartData.reduce((sum, m) => sum + m.monto, 0);

  // Obtener lista de usuarios con su estado de pago
  const getUserPaymentStatus = () => {
    if (!allPayments || allPayments.length === 0) return [];

    // Agrupar por usuario
    const userMap = new Map();

    allPayments.forEach(payment => {
      const userName = payment.user_name || 'Usuario';
      const apartment = payment.apartment || '-';
      const key = `${userName}-${apartment}`;

      if (!userMap.has(key)) {
        userMap.set(key, {
          user_name: userName,
          apartment: apartment,
          paid: 0,
          pending: 0,
          validation: 0,
          totalPaid: 0,
          totalPending: 0
        });
      }

      const user = userMap.get(key);
      if (payment.status === 'Paid') {
        user.paid++;
        user.totalPaid += payment.amount;
      } else if (payment.status === 'Pending') {
        user.pending++;
        user.totalPending += payment.amount;
      } else if (payment.status === 'Validation') {
        user.validation++;
      }
    });

    return Array.from(userMap.values()).sort((a, b) => b.pending - a.pending);
  };

  const userPaymentStatus = getUserPaymentStatus();

  if (loading) {
    return (
      <Container fluid className="inicio-dashboard-cohesity">
        <div className="cohesity-loading">
          <Spinner animation="border" variant="info" />
          <p>Cargando dashboard...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="inicio-dashboard-cohesity">
      {/* Modal de Aviso */}
      <NoticeModal
        show={showNoticeModal}
        onClose={() => setShowNoticeModal(false)}
        notice={selectedNotice}
      />

      {/* ========== FILA 1: Header de Bienvenida ========== */}
      <Row className="mb-4">
        <Col>
          <div className="cohesity-welcome-banner">
            <div className="welcome-content">
              <h1 className="welcome-title">Bienvenido, {user?.name || 'Administrador'}</h1>
              <p className="welcome-date">
                <FaCalendarAlt className="me-2" />
                {getCurrentDate()}
              </p>
            </div>
            <div className="welcome-decoration">
              <div className="decoration-circle c1"></div>
              <div className="decoration-circle c2"></div>
              <div className="decoration-circle c3"></div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILA 2: Métricas Principales con Mini Gauges ========== */}
      <Row className="mb-4">
        {/* Apartamentos - 100% ocupación */}
        <Col lg={3} md={6} className="mb-3">
          <div className="cohesity-metric-card with-gauge" onClick={() => setActiveSection('usuarios')}>
            <MiniGauge
              value={totalApartments}
              maxValue={totalApartments || 1}
              color="#00d4aa"
              label={totalApartments}
            />
            <div className="metric-content">
              <h3 className="metric-value">{totalApartments}</h3>
              <p className="metric-label">Apartamentos</p>
              <span className="metric-sublabel">{totalUsers} residentes</span>
            </div>
          </div>
        </Col>

        {/* Recaudado - % del total */}
        <Col lg={3} md={6} className="mb-3">
          <div className="cohesity-metric-card with-gauge" onClick={() => setActiveSection('contabilidad')}>
            <MiniGauge
              value={totalCollected}
              maxValue={(totalCollected + totalPendingAmount) || 1}
              color="#22c55e"
              label={`${collectionRate.toFixed(0)}%`}
            />
            <div className="metric-content">
              <h3 className="metric-value">${totalCollected.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</h3>
              <p className="metric-label">Recaudado</p>
              <span className="metric-sublabel">{stats.pagosRealizados} pagos</span>
            </div>
          </div>
        </Col>

        {/* Pendiente - % del total (inverso) */}
        <Col lg={3} md={6} className="mb-3">
          <div className="cohesity-metric-card with-gauge warning" onClick={() => setActiveSection('alicuotas')}>
            <MiniGauge
              value={totalPendingAmount}
              maxValue={(totalCollected + totalPendingAmount) || 1}
              color="#f59e0b"
              label={`${(100 - collectionRate).toFixed(0)}%`}
            />
            <div className="metric-content">
              <h3 className="metric-value">${totalPendingAmount.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</h3>
              <p className="metric-label">Pendiente</p>
              <span className="metric-sublabel">{stats.pagosPendientes} por cobrar</span>
            </div>
          </div>
        </Col>

        {/* Tasa de Cobro */}
        <Col lg={3} md={6} className="mb-3">
          <div className="cohesity-metric-card with-gauge" onClick={() => setActiveSection('alicuotas')}>
            <MiniGauge
              value={collectionRate}
              maxValue={100}
              color="#6366f1"
              label={`${collectionRate.toFixed(0)}%`}
            />
            <div className="metric-content">
              <h3 className="metric-value">{collectionRate.toFixed(1)}%</h3>
              <p className="metric-label">Tasa de Cobro</p>
              <span className="metric-sublabel">Meta: 100%</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILA 3: Estado de Pagos (Donut) + Distribución por Usuario ========== */}
      <Row className="mb-4">
        <Col lg={4} className="mb-3">
          <div className="cohesity-chart-card">
            <div className="chart-card-header">
              <h3><FaChartPie className="me-2" /> Estado de Pagos</h3>
              <span className="header-badge">{stats.total} total</span>
            </div>
            <DonutChart
              data={paymentStatusData}
              centerValue={stats.total}
              centerLabel="Pagos"
              colors={[COLORS.success, COLORS.warning, COLORS.danger]}
              height={160}
            />
            <div className="chart-legend compact">
              <div className="legend-row">
                <div className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: COLORS.success }}></span>
                  <span>Pagados: {stats.pagosRealizados}</span>
                  <span className="legend-amount">${totalCollected.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              {stats.pendientesAprobacion > 0 && (
                <div className="legend-row">
                  <div className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: COLORS.warning }}></span>
                    <span>Validación: {stats.pendientesAprobacion}</span>
                  </div>
                </div>
              )}
              <div className="legend-row">
                <div className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: COLORS.danger }}></span>
                  <span>Pendientes: {stats.pagosPendientes}</span>
                  <span className="legend-amount">${totalPendingAmount.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col lg={8} className="mb-3">
          <div className="cohesity-table-card">
            <div className="table-card-header">
              <div className="header-left">
                <FaUsers className="header-icon text-primary" />
                <h3>Distribución por Usuario</h3>
              </div>
              <span className="view-all-link" onClick={() => setActiveSection('alicuotas')}>
                Ver detalles →
              </span>
            </div>
            <div className="table-responsive" style={{ maxHeight: '280px', overflowY: 'auto' }}>
              <table className="cohesity-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Apartamento</th>
                    <th>Pagado</th>
                    <th>Pendiente</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {userPaymentStatus.length > 0 ? (
                    userPaymentStatus.map((user, index) => (
                      <tr key={index}>
                        <td>
                          <div className="user-cell">
                            <div className={`user-avatar ${user.pending > 0 ? 'warning' : 'success'}`}>
                              {user.user_name[0]}
                            </div>
                            <span>{user.user_name}</span>
                          </div>
                        </td>
                        <td>{user.apartment}</td>
                        <td className="amount-cell success">
                          ${user.totalPaid.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                        </td>
                        <td className={`amount-cell ${user.totalPending > 0 ? 'danger' : ''}`}>
                          ${user.totalPending.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                        </td>
                        <td>
                          {user.pending === 0 ? (
                            <span className="status-badge paid">✓ Al día</span>
                          ) : user.validation > 0 ? (
                            <span className="status-badge validation">⏳ Validando</span>
                          ) : (
                            <span className="status-badge pending">⚠ Debe</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="empty-cell">No hay datos de usuarios</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILA 4: Gráfico de Barras + Avisos ========== */}
      <Row className="mb-4">
        <Col lg={8} className="mb-3">
          <div className="cohesity-chart-card large">
            <div className="chart-card-header">
              <div>
                <h3><FaChartBar className="me-2" /> Ingresos Mensuales</h3>
                <p className="chart-subtitle">Recaudación de los últimos 5 meses</p>
              </div>
              <Badge bg="success" className="total-badge">
                ${totalMonthlyRevenue.toLocaleString('es-ES', { minimumFractionDigits: 2 })} total
              </Badge>
            </div>
            <div className="bar-chart-container">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthlyChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" tickFormatter={(value) => `$${value}`} />
                  <Tooltip
                    formatter={(value) => [`$${value.toLocaleString('es-ES', { minimumFractionDigits: 2 })}`, 'Recaudado']}
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#e2e8f0'
                    }}
                  />
                  <Bar
                    dataKey="monto"
                    fill="url(#colorGradient)"
                    radius={[8, 8, 0, 0]}
                  />
                  <defs>
                    <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00d4aa" />
                      <stop offset="100%" stopColor="#00b894" />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-footer-stats">
              <div className="footer-stat">
                <span className="stat-icon">📅</span>
                <div>
                  <p className="stat-label">Último Pago</p>
                  <p className="stat-value">{userInfo.ultimoPago}</p>
                </div>
              </div>
              <div className="footer-stat highlight">
                <span className="stat-icon">💰</span>
                <div>
                  <p className="stat-label">Promedio</p>
                  <p className="stat-value text-success">
                    ${(totalMonthlyRevenue / 5).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
              <div className="footer-stat">
                <span className="stat-icon">📈</span>
                <div>
                  <p className="stat-label">Este Mes</p>
                  <p className="stat-value">${monthlyIncome.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col lg={4} className="mb-3">
          <div className="cohesity-notices-card">
            <div className="notices-header">
              <h3><FaBell className="me-2" /> Avisos</h3>
              <Badge bg="primary" className="notices-count">{notificaciones.length}</Badge>
            </div>
            <div className="notices-list">
              {notificaciones.length === 0 ? (
                <div className="empty-notices">
                  <FaBell className="empty-icon" />
                  <p>No hay avisos nuevos</p>
                  <button
                    className="create-notice-btn"
                    onClick={() => setActiveSection('avisos')}
                  >
                    + Crear aviso
                  </button>
                </div>
              ) : (
                notificaciones.slice(0, 5).map((notif, index) => (
                  <div
                    key={index}
                    className="notice-item clickable"
                    onClick={() => handleNoticeClick(notif)}
                  >
                    <div className="notice-indicator"></div>
                    <div className="notice-content">
                      <h4 className="notice-title">{notif.titulo || notif.title}</h4>
                      <p className="notice-date">
                        {formatDate(notif.fecha || notif.created_at)}
                      </p>
                    </div>
                    <span className="notice-arrow">→</span>
                  </div>
                ))
              )}
            </div>
            {notificaciones.length > 0 && (
              <button
                className="view-all-notices-btn"
                onClick={() => setActiveSection('avisos')}
              >
                Ver todos →
              </button>
            )}
          </div>
        </Col>
      </Row>

      {/* ========== FILA 5: Resumen de Pagos (Pie Chart Horizontal) ========== */}
      <Row>
        <Col lg={12}>
          <div className="cohesity-summary-card" onClick={() => setActiveSection('alicuotas')}>
            <div className="summary-chart-section">
              <ResponsiveContainer width="100%" height={120}>
                <PieChart>
                  <Pie
                    data={paymentStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={50}
                    paddingAngle={3}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    <Cell fill={COLORS.success} />
                    <Cell fill={COLORS.warning} />
                    <Cell fill={COLORS.danger} />
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [value, name]}
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#e2e8f0'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="summary-total">
                <span className="summary-total-value">{stats.total}</span>
                <span className="summary-total-label">Total</span>
              </div>
            </div>
            <div className="summary-legend-section">
              <div className="summary-legend-item">
                <FaCheckCircle className="legend-icon success" />
                <div className="legend-info">
                  <span className="legend-value">{stats.pagosRealizados}</span>
                  <span className="legend-label">Completados</span>
                </div>
                <span className="legend-amount success">${totalCollected.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="summary-legend-item">
                <FaClock className="legend-icon warning" />
                <div className="legend-info">
                  <span className="legend-value">{stats.pendientesAprobacion}</span>
                  <span className="legend-label">Validación</span>
                </div>
                <span className="legend-amount warning">En proceso</span>
              </div>
              <div className="summary-legend-item">
                <FaExclamationCircle className="legend-icon danger" />
                <div className="legend-info">
                  <span className="legend-value">{stats.pagosPendientes}</span>
                  <span className="legend-label">Pendientes</span>
                </div>
                <span className="legend-amount danger">${totalPendingAmount.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Inicio;
